export const DASHBOARD_SAVED_FILTERS_STORAGE_KEY = 'dashboardSavedFilters';
export const DASHBOARD_SAVED_FILTERS_VERSION = 1;

export const EMPTY_FILTER_SNAPSHOT = {
  clientId: '',
  dateFrom: null,
  dateTo: null,
  invoiceDateFrom: null,
  invoiceDateTo: null,
  dispatcherId: '',
  adminId: '',
  priority: '',
};

const createEmptyStorage = () => ({
  version: DASHBOARD_SAVED_FILTERS_VERSION,
  defaultId: null,
  searches: [],
});

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

export const getDashboardSavedFiltersStorageKey = (companyId) => {
  if (companyId == null || companyId === '') return null;
  return `${DASHBOARD_SAVED_FILTERS_STORAGE_KEY}:${companyId}`;
};

/** One-time: move legacy unscoped key into the first company bucket that loads it. */
const migrateLegacySavedFiltersIfNeeded = (companyId) => {
  const key = getDashboardSavedFiltersStorageKey(companyId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(key)) return;
    const legacy = window.localStorage.getItem(DASHBOARD_SAVED_FILTERS_STORAGE_KEY);
    if (!legacy) return;
    window.localStorage.setItem(key, legacy);
    window.localStorage.removeItem(DASHBOARD_SAVED_FILTERS_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
};

export const parseDateField = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const serializeDateField = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  if (typeof value === 'string' && value.length > 0) {
    const parsed = parseDateField(value);
    return parsed ? parsed.toISOString() : null;
  }
  return null;
};

export const captureFilterSnapshot = (filterState) => ({
  clientId: typeof filterState.clientId === 'string' ? filterState.clientId : '',
  dateFrom: filterState.dateFrom instanceof Date ? filterState.dateFrom.toISOString() : null,
  dateTo: filterState.dateTo instanceof Date ? filterState.dateTo.toISOString() : null,
  invoiceDateFrom: filterState.invoiceDateFrom instanceof Date
    ? filterState.invoiceDateFrom.toISOString()
    : null,
  invoiceDateTo: filterState.invoiceDateTo instanceof Date
    ? filterState.invoiceDateTo.toISOString()
    : null,
  dispatcherId: typeof filterState.dispatcherId === 'string' ? filterState.dispatcherId : '',
  adminId: typeof filterState.adminId === 'string' ? filterState.adminId : '',
  priority: typeof filterState.priority === 'string' ? filterState.priority : '',
});

export const normalizeFilterSnapshot = (snapshot) => {
  const source = snapshot && typeof snapshot === 'object' ? snapshot : {};

  return {
    clientId: typeof source.clientId === 'string' ? source.clientId : '',
    dateFrom: serializeDateField(source.dateFrom),
    dateTo: serializeDateField(source.dateTo),
    invoiceDateFrom: serializeDateField(source.invoiceDateFrom),
    invoiceDateTo: serializeDateField(source.invoiceDateTo),
    dispatcherId: typeof source.dispatcherId === 'string' ? source.dispatcherId : '',
    adminId: typeof source.adminId === 'string' ? source.adminId : '',
    priority: typeof source.priority === 'string' ? source.priority : '',
  };
};

export const sanitizeFilterSnapshot = (
  snapshot,
  { clientOptions, adminOptions, dispatcherOptions } = {},
) => {
  const normalized = normalizeFilterSnapshot(snapshot);

  // Strict when options arrays are provided. Omit to skip (list not ready).
  if (Array.isArray(clientOptions)) {
    const clientIds = new Set(clientOptions.map((client) => String(client.id)));
    if (normalized.clientId && !clientIds.has(String(normalized.clientId))) {
      normalized.clientId = '';
    }
  }

  if (Array.isArray(adminOptions)) {
    const adminIds = new Set(adminOptions.map((admin) => String(admin.id)));
    if (normalized.adminId && !adminIds.has(String(normalized.adminId))) {
      normalized.adminId = '';
    }
  }

  if (Array.isArray(dispatcherOptions)) {
    const dispatcherIds = new Set(dispatcherOptions.map((dispatcher) => String(dispatcher.id)));
    if (normalized.dispatcherId && !dispatcherIds.has(String(normalized.dispatcherId))) {
      normalized.dispatcherId = '';
    }
  }

  const validPriorities = new Set(['1', '2', '3', '4']);
  if (normalized.priority && !validPriorities.has(normalized.priority)) {
    normalized.priority = '';
  }

  return normalized;
};

export const snapshotsMatch = (left, right) => {
  const a = normalizeFilterSnapshot(left);
  const b = normalizeFilterSnapshot(right);

  return (
    a.clientId === b.clientId
    && a.dateFrom === b.dateFrom
    && a.dateTo === b.dateTo
    && a.invoiceDateFrom === b.invoiceDateFrom
    && a.invoiceDateTo === b.invoiceDateTo
    && a.dispatcherId === b.dispatcherId
    && a.adminId === b.adminId
    && a.priority === b.priority
  );
};

const normalizeSearchEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  if (!isNonEmptyString(entry.id) || !isNonEmptyString(entry.name)) return null;

  return {
    id: entry.id,
    name: entry.name.trim(),
    createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : new Date().toISOString(),
    updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : new Date().toISOString(),
    filters: normalizeFilterSnapshot(entry.filters),
  };
};

export const normalizeSavedFiltersStorage = (raw) => {
  const empty = createEmptyStorage();
  if (!raw || typeof raw !== 'object') return empty;

  const searches = Array.isArray(raw.searches)
    ? raw.searches.map(normalizeSearchEntry).filter(Boolean)
    : [];

  const defaultId = typeof raw.defaultId === 'string' ? raw.defaultId : null;
  const hasDefault = defaultId && searches.some((search) => search.id === defaultId);

  return {
    version: DASHBOARD_SAVED_FILTERS_VERSION,
    defaultId: hasDefault ? defaultId : null,
    searches,
  };
};

export const loadDashboardSavedFilters = (companyId) => {
  try {
    if (!companyId) return createEmptyStorage();
    migrateLegacySavedFiltersIfNeeded(companyId);
    const key = getDashboardSavedFiltersStorageKey(companyId);
    const stored = window.localStorage.getItem(key);
    if (!stored) return createEmptyStorage();
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return createEmptyStorage();
    if (parsed.version !== DASHBOARD_SAVED_FILTERS_VERSION) return createEmptyStorage();
    return normalizeSavedFiltersStorage(parsed);
  } catch {
    return createEmptyStorage();
  }
};

export const persistDashboardSavedFilters = (companyId, data) => {
  const normalized = normalizeSavedFiltersStorage(data);
  const key = getDashboardSavedFiltersStorageKey(companyId);
  if (!key) return normalized;
  try {
    window.localStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    // ignore quota / private mode
  }
  return normalized;
};

/**
 * Drop inaccessible clientId from every saved filter for this company and persist when changed.
 * Call after the live client list has loaded (primary-role admin only at the call site).
 */
export const pruneSavedSearchesAgainstClients = (companyId, storage, clientOptions = []) => {
  const normalized = normalizeSavedFiltersStorage(storage);
  if (!companyId) {
    return { storage: normalized, changed: false };
  }

  let changed = false;
  const searches = normalized.searches.map((search) => {
    const sanitizedFilters = sanitizeFilterSnapshot(search.filters, { clientOptions });
    if (search.filters.clientId === sanitizedFilters.clientId) {
      return search;
    }
    changed = true;
    return {
      ...search,
      filters: sanitizedFilters,
      updatedAt: new Date().toISOString(),
    };
  });

  if (!changed) {
    return { storage: normalized, changed: false };
  }

  const nextStorage = persistDashboardSavedFilters(companyId, {
    ...normalized,
    searches,
  });
  return { storage: nextStorage, changed: true };
};

export const createSearchId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `search-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const upsertSearch = (companyId, storage, { id, name, filters, setAsDefault = false }) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Saved filter name is required.');
  }

  const normalized = normalizeSavedFiltersStorage(storage);
  const normalizedFilters = normalizeFilterSnapshot(filters);
  const now = new Date().toISOString();
  let savedSearchId = id || null;

  const existingIndex = id ? normalized.searches.findIndex((search) => search.id === id) : -1;
  const nextId = existingIndex >= 0 ? normalized.searches[existingIndex].id : createSearchId();
  savedSearchId = nextId;

  const nextSearch = {
    id: nextId,
    name: trimmedName,
    createdAt: existingIndex >= 0 ? normalized.searches[existingIndex].createdAt : now,
    updatedAt: now,
    filters: normalizedFilters,
  };

  const searches = existingIndex >= 0
    ? normalized.searches.map((search, index) => (index === existingIndex ? nextSearch : search))
    : [...normalized.searches, nextSearch];

  return {
    storage: persistDashboardSavedFilters(companyId, {
      ...normalized,
      defaultId: setAsDefault ? nextId : normalized.defaultId,
      searches,
    }),
    searchId: savedSearchId,
  };
};

export const deleteSearch = (companyId, storage, searchId) => {
  const normalized = normalizeSavedFiltersStorage(storage);
  return persistDashboardSavedFilters(companyId, {
    ...normalized,
    defaultId: normalized.defaultId === searchId ? null : normalized.defaultId,
    searches: normalized.searches.filter((search) => search.id !== searchId),
  });
};

export const setDefaultSearch = (companyId, storage, searchId) => {
  const normalized = normalizeSavedFiltersStorage(storage);
  const exists = normalized.searches.some((search) => search.id === searchId);
  return persistDashboardSavedFilters(companyId, {
    ...normalized,
    defaultId: exists ? searchId : normalized.defaultId,
  });
};

export const clearDefaultSearch = (companyId, storage) => {
  const normalized = normalizeSavedFiltersStorage(storage);
  return persistDashboardSavedFilters(companyId, {
    ...normalized,
    defaultId: null,
  });
};

export const renameSearch = (companyId, storage, searchId, name) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Saved filter name is required.');
  }

  const normalized = normalizeSavedFiltersStorage(storage);
  return persistDashboardSavedFilters(companyId, {
    ...normalized,
    searches: normalized.searches.map((search) => (
      search.id === searchId
        ? { ...search, name: trimmedName, updatedAt: new Date().toISOString() }
        : search
    )),
  });
};

export const findSearchById = (storage, searchId) => {
  const normalized = normalizeSavedFiltersStorage(storage);
  return normalized.searches.find((search) => search.id === searchId) || null;
};

export const getDefaultSearch = (storage) => {
  const normalized = normalizeSavedFiltersStorage(storage);
  if (!normalized.defaultId) return null;
  return normalized.searches.find((search) => search.id === normalized.defaultId) || null;
};

export const snapshotToFilterState = (snapshot) => {
  const normalized = normalizeFilterSnapshot(snapshot);
  return {
    clientId: normalized.clientId,
    dateFrom: parseDateField(normalized.dateFrom),
    dateTo: parseDateField(normalized.dateTo),
    invoiceDateFrom: parseDateField(normalized.invoiceDateFrom),
    invoiceDateTo: parseDateField(normalized.invoiceDateTo),
    dispatcherId: normalized.dispatcherId,
    adminId: normalized.adminId,
    priority: normalized.priority,
  };
};
